<template>
  <div>
    <ul class="kt-aside__nav">
      <li
        class="kt-aside__nav-item"
        @click="mainStore.navigateToReleaseNotes"
      >
        <a
          id="menu-item-release-notes"
          class="kt-aside__nav-link"
          :class="{'active': hasUnreadChangelogs}"
        >
          <div class="position-relative">
            <FluentMegaphoneLoud28Filled
              width="1.7em"
              height="1.7em"
              style="color: #ffffff;"
            />

            <span
              v-if="hasUnreadChangelogs"
              class="unread-changelogs-dot"
            >
              <span />
            </span>
          </div>
        </a>
      </li>

      <li
        class="kt-aside__nav-item"
        @click="mainStore.toggleProfileToolbar"
      >
        <a
          id="profile_toolbar_toggler_btn"
          class="kt-aside__nav-link"
        >
          <div
            v-if="!profilePicture"
            class="kt-media"
            :class="`user-color-${user.color}`"
          >
            <span>{{ user.initials }}</span>
          </div>

          <div
            v-else
            class="kt-media"
            style="background: transparent !important;"
          >
            <img
              :src="profilePicture"
              loading="lazy"
              decoding="async"
              class="kt-sidemenu-profile-picture"
              alt="Profielfoto"
            >
          </div>
        </a>
      </li>
    </ul>

    <profile-toolbar />
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import ProfileToolbar from '@/components/layout/ProfileToolbar.vue'
import type CustomWindow from '@/misc/custom-window'
import type User from '@/types/User'
import { useMainStore } from '@/stores/main-store'
import FluentMegaphoneLoud28Filled from '@/components/icons/FluentMegaphoneLoud28Filled.vue'
import { type RemovableRef, useStorage } from '@vueuse/core'
import LanguageType from '@/enums/LanguageType'

const props = defineProps<{
  tenantLanguages: { id: string, type: string }[];
  propUser?: User;
  defaultCompanyName: string;
}>()

declare const window: CustomWindow

const hasUnreadChangelogs = useStorage(`unread-changelogs-${window.user_id}`, false, sessionStorage) as RemovableRef<boolean>

const mainStore = useMainStore()

const {
  user,
  profilePicture,
  companyName,
  languages,
  primaryLanguage,
  selectedLanguage
} = storeToRefs(mainStore)

onBeforeMount(() => {
  user.value = props.propUser ?? window.user
  companyName.value = props.defaultCompanyName
  hasUnreadChangelogs.value = window.user_has_unread_changelogs

  if (user.value.profile_picture_big) {
    profilePicture.value = user.value.profile_picture_big
  }

  primaryLanguage.value = props.tenantLanguages.find(language => language.type === LanguageType.PRIMARY)?.id ?? null
  selectedLanguage.value = primaryLanguage.value
  languages.value = props.tenantLanguages
})

onMounted(() => {
  if (!window.Echo) return

  window.Echo.private(`App.Models.User.${window.user_id}`)
    .notification((notification: any) => {
      if (notification.type === 'App\\Notifications\\ChangelogPublished') hasUnreadChangelogs.value = true
    })
})
</script>

<style scoped lang="scss">
@import "resources/sass/variables";

.kt-aside__primary .kt-aside__bottom .kt-aside__nav .kt-aside__nav-item:hover #profile_toolbar_toggler_btn {
  background-color: transparent !important;
  cursor: pointer;
}

.kt-aside__primary .kt-aside__bottom .kt-aside__nav .kt-aside__nav-item:hover #menu-item-release-notes {
  cursor: pointer;
}

.unread-changelogs-dot {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  right: -14px;
  top: -14px;
  height: 16px;
  width: 16px;
  border-radius: 50%;
  background-color: #00606f;

  > span {
    display: inline-block;
    height: 10px;
    width: 10px;
    border-radius: 50%;
    background-color: #F08825;
    animation: blinker 3s linear infinite;
  }
}

@keyframes blinker {
  50% {
    opacity: 0;
  }
}

#menu-item-release-notes {
  &:hover {
    background-color: lighten(#1B7B88, 5%);
  }
}
</style>
